import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { forkJoin, Observable, of } from "rxjs";
import { debounceTime, distinctUntilChanged, map, tap } from "rxjs/operators";
import { baseUrl } from "../helpers/util";
import { IReviewData } from "../model/interfaces";
import {
  AssessmentResult,
  Assignment,
  FormattedAssignmentLogEvent,
  ItemFeedback,
  TestSessionStatus,
  User,
} from "../model/model";
import { AssessmentResultService } from "../services/assessment-result.service";
import { AssignmentService } from "../services/assignment.service";

export class LoadAssessmentResultsAction {
  static type = "[Assessment result] Load by assigmentId";
  constructor(public assignmentId: string) {}
}
export class LoadReviewUrlAction {
  static type = "[Assessment result] Load review url";
  constructor(public itemId: string) {}
}
export class LoadFirstReviewUrlAction {
  static type = "[Assessment result] Load first review url";
}
export class CloseReviewAction {
  static type = "[Assessment result] close the review (not fullscreen)";
}
export class ShowAssignmentAction {
  static type = "[Assessment result] Show action popup to change status";
}
export class HideAssignmentAction {
  static type = "[Assessment result] Hide action popup to change status";
}
export class ShowLogAction {
  static type = "[Assessment result] Show log events";
}
export class HideLogAction {
  static type = "[Assessment result] Hide log events";
}
export class ModifiedAssessmentResultAction {
  static type = "[Assessment result] Modified";
  constructor(public assignments: Assignment[]) {}
}
export class SelectAssessmentResultAction {
  static type = "[Assessment result] Select assessment result";
  constructor(public assignmentResultId: string) {}
}

export interface AssignmentDetailsStateModel {
  assignmentId: string;
  candidate: User;
  selectedResult: string;
  results: AssessmentResult[];
  loaded: boolean;
  currentReview: IReviewData;
  showAction: boolean;
  showLog: boolean;
  logEvents: Array<FormattedAssignmentLogEvent>;
  loadingLog: boolean;
}

export interface ReviewUrls {
  correctUrl: string;
  reviewUrl: string;
}

export interface ResultData {
  result: AssessmentResult;
  hasData: boolean;
}

export const initialState: AssignmentDetailsStateModel = {
  loaded: false,
  results: null,
  currentReview: null,
  selectedResult: "",
  showAction: false,
  showLog: false,
  logEvents: [],
  loadingLog: false,
  assignmentId: "",
  candidate: null,
};

const onlyQtiPlayerResultsFilter = (r) => {
  return (
    r &&
    (r.status === TestSessionStatus.Failed ||
      r.status === TestSessionStatus.Passed) &&
    !r.isOffline
  );
};

@State<AssignmentDetailsStateModel>({
  name: "assignmentdetails",
  defaults: initialState,
})
@Injectable()
export class AssignmentDetailsState {
  constructor(
    private resultService: AssessmentResultService,
    private assignmentService: AssignmentService
  ) {}

  @Selector()
  static urls(state: AssignmentDetailsStateModel): ReviewUrls {
    if (!state.currentReview) {
      return {
        correctUrl: "assets/loader.html",
        reviewUrl: "assets/loader.html",
      };
    }

    return {
      correctUrl: state.currentReview.correctUrl,
      reviewUrl: state.currentReview.reviewUrl,
    };
  }

  @Selector()
  static selectedResult(state: AssignmentDetailsStateModel) {
    const result = state.results
      .filter(onlyQtiPlayerResultsFilter)
      .find((r) => r.id === state.selectedResult);
    return {
      result: result,
      hasData: !!result,
    } as ResultData;
  }
  @Selector()
  static displaySetting(state: AssignmentDetailsStateModel) {
    return {
      showAction: state.showAction,
      showLog: state.showLog,
      loaded: state.loaded,
    };
  }

  @Action(LoadAssessmentResultsAction)
  loadAssessmentResults(
    ctx: StateContext<AssignmentDetailsStateModel>,
    action: LoadAssessmentResultsAction
  ) {
    ctx.patchState({
      loaded: false,
      assignmentId: action.assignmentId,
      currentReview: null,
    });
  }
  @Action(ShowLogAction)
  showLog(ctx: StateContext<AssignmentDetailsStateModel>) {
    ctx.patchState({ loadingLog: true, showLog: true });
  }
  @Action(ShowLogAction)
  showingLog(ctx: StateContext<AssignmentDetailsStateModel>) {
    return this.assignmentService
      .getFormattedLog(ctx.getState().assignmentId)
      .pipe(
        tap((log) => ctx.patchState({ loadingLog: false, logEvents: log }))
      );
  }
  @Action(HideLogAction)
  hideLog(ctx: StateContext<AssignmentDetailsStateModel>) {
    ctx.patchState({ loadingLog: true, showLog: false });
  }
  @Action(ShowAssignmentAction)
  showAssignmentAction(ctx: StateContext<AssignmentDetailsStateModel>) {
    ctx.patchState({ showAction: true });
  }
  @Action(HideAssignmentAction)
  hideAssignmentAction(ctx: StateContext<AssignmentDetailsStateModel>) {
    ctx.patchState({ showAction: false });
  }
  @Action(CloseReviewAction)
  closeReview(ctx: StateContext<AssignmentDetailsStateModel>) {
    ctx.setState({ ...initialState });
  }
  @Action(LoadAssessmentResultsAction)
  loadedAssignment(
    ctx: StateContext<AssignmentDetailsStateModel>,
    action: LoadAssessmentResultsAction
  ) {
    return forkJoin(
      this.resultService.getResults(action.assignmentId),
      this.resultService.getUserByAssignment(action.assignmentId)
    ).pipe(
      tap(([results, candidate]) => {
        const sortedResults = results.sort(
          (a: AssessmentResult, b: AssessmentResult) => {
            return (
              new Date(b.lastModified).getTime() -
              new Date(a.lastModified).getTime()
            );
          }
        );
        ctx.patchState({
          loaded: true,
          candidate: candidate,
          results: sortedResults,
          selectedResult:
            sortedResults &&
            sortedResults.filter(onlyQtiPlayerResultsFilter).length > 0
              ? sortedResults.filter(onlyQtiPlayerResultsFilter)[0].id
              : "",
        });
        ctx.dispatch(new LoadFirstReviewUrlAction());
      })
    );
  }
  @Action(ModifiedAssessmentResultAction)
  reloadedAssignment(
    ctx: StateContext<AssignmentDetailsStateModel>,
    action: ModifiedAssessmentResultAction
  ) {
    const modifiedAssignment = action.assignments.find(
      (a) => a.id === ctx.getState().assignmentId
    );
    if (modifiedAssignment) {
      return ctx.dispatch(
        new LoadAssessmentResultsAction(modifiedAssignment.id)
      );
    }
  }
  @Action([LoadReviewUrlAction, LoadFirstReviewUrlAction])
  loadReview(ctx: StateContext<AssignmentDetailsStateModel>, action: any) {
    const itemId = <LoadReviewUrlAction>action
      ? (<LoadReviewUrlAction>action).itemId
      : null;
    const currentState = ctx.getState();
    if (
      !currentState.currentReview ||
      (currentState.currentReview && itemId !== currentState.currentReview.id)
    ) {
      ctx.patchState({
        currentReview: {
          ...ctx.getState().currentReview,
          reviewUrl: "assets/loader.html",
          correctUrl: "assets/loader.html",
        },
      });
    }
  }

  @Action(LoadFirstReviewUrlAction)
  loadfirst(ctx: StateContext<AssignmentDetailsStateModel>) {
    const state = ctx.getState();
    const sr =
      state.selectedResult && state.results
        ? state.results.find((r) => r.id === state.selectedResult)
        : null;
    if (
      sr &&
      sr.items &&
      sr.items.length > 0 &&
      (sr.status === TestSessionStatus.Failed ||
        sr.status === TestSessionStatus.Passed)
    ) {
      return this.loadReviewState(ctx, sr.items[0].itemId);
    }
  }

  @Action(LoadReviewUrlAction)
  loadingReview(
    ctx: StateContext<AssignmentDetailsStateModel>,
    action: LoadReviewUrlAction
  ) {
    return this.loadReviewState(ctx, action.itemId);
  }

  @Action(SelectAssessmentResultAction)
  selectAssessmentResult(
    ctx: StateContext<AssignmentDetailsStateModel>,
    action: SelectAssessmentResultAction
  ) {
    ctx.patchState({
      selectedResult: action.assignmentResultId,
      currentReview: null,
    });
    ctx.dispatch(new LoadFirstReviewUrlAction());
  }

  private loadReviewState(
    ctx: StateContext<AssignmentDetailsStateModel>,
    itemId: string
  ): Observable<IReviewData> {
    const state = ctx.getState();
    const selectedResult =
      state.selectedResult && state.results
        ? state.results.find((r) => r.id === state.selectedResult)
        : null;
    return this.getReviewData(itemId, selectedResult, state.currentReview).pipe(
      tap((r) => ctx.patchState({ currentReview: r }))
    );
  }

  // private getReviewData(itemId: string, assessmentResult: AssessmentResult, latestReview: IReviewData)
  //     : Observable<IReviewData> {
  //     const item = assessmentResult.items.find(i => i.itemId === itemId);
  //     return ((latestReview.baseSessionUrl) ?
  //         of(this.reviewUrl(`${latestReview.baseSessionUrl}/player/show/${item.itemId}`,
  //             item.itemId, item.rawScore !== 0)) :
  //         this.getReviewUrl(itemId, assessmentResult)
  //             .pipe(map(r => this.reviewUrl(r, itemId, item && item.rawScore > 0))))
  //         .pipe(debounceTime(300));
  // }

  private getReviewData(
    itemId: string,
    assessmentResult: AssessmentResult,
    latestReview: IReviewData
  ): Observable<IReviewData> {
    const item =
      itemId === "first"
        ? assessmentResult.items[0]
        : assessmentResult.items.find((i) => i.itemId === itemId);
    const index = assessmentResult.items.indexOf(item);
    const prev = index > 0 ? assessmentResult.items[index - 1].itemId : "";
    const next =
      1 + index < assessmentResult.items.length
        ? assessmentResult.items[index + 1].itemId
        : "";
    return (
      latestReview.reviewUrl &&
      latestReview.reviewUrl.toString().indexOf("http") !== -1
        ? of(
            this.reviewUrl(
              `${this.baseUrl(latestReview.reviewUrl.toString())}/player/show/${
                item.itemId
              }`,
              item.itemId,
              item.rawScore !== 0,
              prev,
              next,
              item.itemFeedbacks
            )
          )
        : this.getReviewUrl(item.itemId, assessmentResult).pipe(
            map((r) =>
              this.reviewUrl(
                r,
                itemId,
                item && item.rawScore > 0,
                prev,
                next,
                item && item.itemFeedbacks
              )
            )
          )
    ).pipe(debounceTime(300));
  }

  private baseUrl = (url: string): string => {
    const pathArray = url.split("/");
    const protocol = pathArray[0];
    const host = pathArray[2];
    return protocol + "//" + host;
  };

  private getReviewUrl = (
    itemId: string,
    assessmentResult: AssessmentResult
  ): Observable<string> =>
    this.resultService
      .reviewItem(assessmentResult.qtiPlayerSessionId, itemId)
      .pipe(
        debounceTime(50),
        distinctUntilChanged(),
        map((uri: string) => (uri ? uri : "about:blank"))
      );

  // private reviewUrl = (url: string, itemId: string, isCorrect): IReviewData =>
  //     ({
  //         id: itemId,
  //         reviewUrl: url.replace(/"/g, ''),
  //         correctUrl: isCorrect ? 'assets/correct.html' : `${url}#correct`,
  //         baseSessionUrl: baseUrl(url.replace(/"/g, ''))
  //     })

  private reviewUrl = (
    url: string,
    itemId: string,
    isCorrect,
    prev: string,
    next: string,
    itemFeedbacks: Array<ItemFeedback>
  ): IReviewData => ({
    id: itemId,
    reviewUrl: url.replace(/"/g, ""),
    correctUrl: isCorrect ? "assets/correct.html" : `${url}#correct`,
    nextItem: next,
    previousItem: prev,
    itemFeedbacks: itemFeedbacks,
    showFeedback: itemFeedbacks && itemFeedbacks.length > 0,
    baseSessionUrl: baseUrl(url.replace(/"/g, "")),
  });
}
